import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import { ChevronUp, ChevronDown, Mail, Lock, User, X, Eye, EyeOff, Globe } from 'lucide-react';
import { useI18n } from '../contexts/I18nContext';
import '../styles/Landing.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Supported interface languages for the landing picker. Kept self-contained
// so Landing has no dependency on the authenticated Layout sidebar.
const LANDING_LOCALES = [
  { code: 'en',    label: 'English',        countryCode: 'us' },
  { code: 'pt-BR', label: 'Português (BR)', countryCode: 'br' },
  { code: 'es',    label: 'Español',        countryCode: 'es' },
  { code: 'fr',    label: 'Français',       countryCode: 'fr' },
  { code: 'zh',    label: '中文',            countryCode: 'cn' },
  { code: 'ru',    label: 'Русский',        countryCode: 'ru' },
  { code: 'fa',    label: 'فارسی',          countryCode: 'ir' },
  { code: 'ar',    label: 'العربية',        countryCode: 'sa' },
];

function LanguagePicker({ variant = 'desktop' }) {
  const { locale, setLocale } = useI18n();
  const [open, setOpen] = useState(false);
  const current = LANDING_LOCALES.find((l) => l.code === locale) || LANDING_LOCALES[0];

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
        data-testid="landing-language-picker"
        className={
          variant === 'desktop'
            ? 'flex items-center gap-2 px-3 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 transition text-sm font-medium'
            : 'flex items-center gap-1 px-2 py-1.5 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 transition text-xs font-medium'
        }
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img
          src={`https://flagcdn.com/w40/${current.countryCode}.png`}
          srcSet={`https://flagcdn.com/w80/${current.countryCode}.png 2x`}
          alt={current.label}
          className="w-5 h-4 object-cover rounded-sm"
          loading="lazy"
        />
        <span className="hidden sm:inline">{current.label}</span>
        <Globe className="sm:hidden w-4 h-4" />
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className="absolute right-0 mt-2 w-52 max-h-80 overflow-auto rounded-xl border border-white/10 bg-[#0d1520] shadow-2xl z-50 py-1"
          role="listbox"
        >
          {LANDING_LOCALES.map((lang) => (
            <button
              key={lang.code}
              onMouseDown={(e) => {
                e.preventDefault(); // prevent blur before onClick fires
                setLocale(lang.code);
                setOpen(false);
              }}
              data-testid={`landing-locale-${lang.code}`}
              className={`w-full flex items-center gap-3 px-3 py-2 text-sm text-left hover:bg-white/5 transition ${
                lang.code === locale ? 'bg-[#ffcc33]/10 text-[#ffcc33]' : 'text-white/90'
              }`}
              role="option"
              aria-selected={lang.code === locale}
            >
              <img
                src={`https://flagcdn.com/w40/${lang.countryCode}.png`}
                srcSet={`https://flagcdn.com/w80/${lang.countryCode}.png 2x`}
                alt={lang.label}
                className="w-5 h-4 object-cover rounded-sm flex-shrink-0"
                loading="lazy"
              />
              <span className="flex-1 truncate">{lang.label}</span>
              {lang.code === locale && <span className="text-xs">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Auth Modal Component
function AuthModal({ isOpen, onClose, onSuccess, initialResetToken = '' }) {
  const { login, loginWithPassword, register, checkAuth } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [mode, setMode] = useState(initialResetToken ? 'reset' : 'choice'); // 'choice', 'login', 'register', 'forgot', 'reset'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [name, setName] = useState('');
  const [resetToken, setResetToken] = useState(initialResetToken);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  // Update reset token if initialResetToken changes
  React.useEffect(() => {
    if (initialResetToken) {
      setResetToken(initialResetToken);
      setMode('reset');
    }
  }, [initialResetToken]);

  if (!isOpen) return null;

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      const user = await loginWithPassword(email, password);
      toast.success(t('landing.auth.welcomeBack'));
      onClose();
      
      // Check onboarding status
      const meResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      const destination = meResponse.data.onboarding_completed ? '/dashboard' : '/onboarding';
      navigate(destination);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccessMessage('');
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/forgot-password`, { email });
      setSuccessMessage(response.data.message);
      
      // For testing, if we got a reset token back, show it (remove in production)
      if (response.data.reset_token) {
        setResetToken(response.data.reset_token);
        setMode('reset');
      }
    } catch (err) {
      setError(err.response?.data?.detail || t('landing.auth.resetEmailFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (newPassword.length < 6) {
      setError(t('landing.auth.passwordTooShort'));
      setLoading(false);
      return;
    }
    
    try {
      await axios.post(`${BACKEND_URL}/api/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      toast.success(t('landing.auth.passwordResetSuccess'));
      setMode('login');
      setNewPassword('');
      setResetToken('');
    } catch (err) {
      setError(err.response?.data?.detail || t('landing.auth.passwordResetFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    if (password.length < 6) {
      setError(t('landing.auth.passwordTooShort'));
      setLoading(false);
      return;
    }
    
    try {
      await register(email, password, name);
      toast.success(t('landing.auth.accountCreated'));
      onClose();
      navigate('/onboarding');
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = () => {
    onClose();
    login();
  };

  const resetModal = () => {
    setMode('choice');
    setEmail('');
    setPassword('');
    setNewPassword('');
    setName('');
    setResetToken('');
    setError('');
    setSuccessMessage('');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="relative w-full max-w-md mx-4 bg-[#0d1520] border border-white/10 rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <h2 className="text-xl font-bold text-white">
            {mode === 'choice' && 'Sign In to iLing'}
            {mode === 'login' && 'Sign In with Email'}
            {mode === 'register' && 'Create Account'}
            {mode === 'forgot' && 'Reset Password'}
            {mode === 'reset' && 'Set New Password'}
          </h2>
          <button 
            onClick={() => { onClose(); resetModal(); }}
            className="p-2 rounded-lg hover:bg-white/10 transition"
          >
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        <div className="p-6">
          {/* Choice Mode */}
          {mode === 'choice' && (
            <div className="space-y-4">
              <p className="text-slate-400 text-sm text-center mb-6">
                {t('landing.auth.chooseHowToSignIn')}
              </p>
              
              <button
                onClick={handleGoogleSignIn}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-white text-gray-800 rounded-xl font-medium hover:bg-gray-100 transition"
              >
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                  <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                  <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                  <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                </svg>
                {t('landing.auth.continueWithGoogle')}
              </button>
              
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/10"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-3 bg-[#0d1520] text-slate-500">{t('landing.auth.or')}</span>
                </div>
              </div>
              
              <button
                onClick={() => setMode('login')}
                className="w-full flex items-center justify-center gap-3 px-4 py-3 bg-[#3A7BFF] text-white rounded-xl font-medium hover:bg-[#4c89ff] transition"
              >
                <Mail className="w-5 h-5" />
                {t('landing.auth.signInWithEmail')}
              </button>
              
              <p className="text-center text-slate-500 text-sm">
                {t('landing.auth.noAccount')}{' '}
                <button 
                  onClick={() => setMode('register')}
                  className="text-[#ffcc33] hover:underline"
                >
                  {t('landing.auth.createOne')}
                </button>
              </p>
            </div>
          )}

          {/* Login Form */}
          {mode === 'login' && (
            <form onSubmit={handlePasswordLogin} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">{t('landing.auth.email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder={t('landing.auth.emailPlaceholder')}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">{t('landing.auth.password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder={t('landing.auth.passwordPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#3A7BFF] text-white rounded-xl font-medium hover:bg-[#4c89ff] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('landing.auth.signingIn') : t('landing.auth.signIn')}
              </button>
              
              <div className="flex items-center justify-between text-sm">
                <button 
                  type="button"
                  onClick={() => { setMode('choice'); setError(''); }}
                  className="text-slate-400 hover:text-white"
                >
                  ← {t('landing.auth.back')}
                </button>
                <button 
                  type="button"
                  onClick={() => { setMode('forgot'); setError(''); setSuccessMessage(''); }}
                  className="text-slate-400 hover:text-white"
                >
                  {t('landing.auth.forgotPassword')}
                </button>
              </div>
              
              <p className="text-center text-slate-500 text-sm">
                {t('landing.auth.noAccount')}{' '}
                <button 
                  type="button"
                  onClick={() => { setMode('register'); setError(''); }}
                  className="text-[#ffcc33] hover:underline"
                >
                  {t('landing.auth.createOne')}
                </button>
              </p>
            </form>
          )}

          {/* Forgot Password Form */}
          {mode === 'forgot' && (
            <form onSubmit={handleForgotPassword} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              {successMessage && (
                <div className="p-3 bg-green-500/10 border border-green-500/30 rounded-lg text-green-400 text-sm">
                  {successMessage}
                </div>
              )}
              
              <p className="text-slate-400 text-sm">
                {t('landing.auth.forgotIntro')}
              </p>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">{t('landing.auth.email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder={t('landing.auth.emailPlaceholder')}
                    required
                  />
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#3A7BFF] text-white rounded-xl font-medium hover:bg-[#4c89ff] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('landing.auth.sending') : t('landing.auth.sendResetInstructions')}
              </button>
              
              <button 
                type="button"
                onClick={() => { setMode('login'); setError(''); setSuccessMessage(''); }}
                className="w-full text-center text-slate-400 hover:text-white text-sm"
              >
                ← {t('landing.auth.backToSignIn')}
              </button>
            </form>
          )}

          {/* Reset Password Form */}
          {mode === 'reset' && (
            <form onSubmit={handleResetPassword} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <p className="text-slate-400 text-sm">
                Enter your new password below.
              </p>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">New Password</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder="Min 6 characters"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#ffcc33] text-[#08101a] rounded-xl font-medium hover:bg-[#ffd85e] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('landing.auth.resetting') : t('landing.auth.resetPassword')}
              </button>
            </form>
          )}

          {/* Register Form */}
          {mode === 'register' && (
            <form onSubmit={handleRegister} className="space-y-4">
              {error && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
                  {error}
                </div>
              )}
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">{t('landing.auth.name')}</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder={t('landing.auth.namePlaceholder')}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">{t('landing.auth.email')}</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder={t('landing.auth.emailPlaceholder')}
                    required
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm text-slate-400 mb-1.5">{t('landing.auth.password')}</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-slate-900/50 border border-white/10 rounded-xl text-white placeholder-slate-500 focus:outline-none focus:border-[#3A7BFF] transition"
                    placeholder={t('landing.auth.newPasswordPlaceholder')}
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500 mt-1">{t('landing.auth.minChars')}</p>
              </div>
              
              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 bg-[#ffcc33] text-[#08101a] rounded-xl font-medium hover:bg-[#ffd85e] transition disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? t('landing.auth.creatingAccount') : t('landing.auth.createAccount')}
              </button>
              
              <div className="flex items-center justify-between text-sm">
                <button 
                  type="button"
                  onClick={() => { setMode('choice'); setError(''); }}
                  className="text-slate-400 hover:text-white"
                >
                  ← {t('landing.auth.back')}
                </button>
                <button 
                  type="button"
                  onClick={() => { setMode('login'); setError(''); }}
                  className="text-[#3A7BFF] hover:underline"
                >
                  {t('landing.auth.alreadyHaveAccount')}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default function Landing() {
  const { login, checkAuth } = useAuth();
  const navigate = useNavigate();
  const { t, tRaw } = useI18n();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [initialResetToken, setInitialResetToken] = useState('');

  // Check for reset_token in URL on mount
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const resetToken = params.get('reset_token');
    if (resetToken) {
      setInitialResetToken(resetToken);
      setShowAuthModal(true);
      // Clean URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Dev Login bypass for testing (kept for admin access)
  const handleDevLogin = async () => {
    try {
      const response = await axios.post(`${BACKEND_URL}/api/auth/dev-login`, {
        email: 'dev@iling.local',
        name: 'Dev User',
      }, { withCredentials: true });
      toast.success(t('landing.auth.loggedInAsDev'));
      await checkAuth();
      
      // Check onboarding status from the user data
      const meResponse = await axios.get(`${BACKEND_URL}/api/auth/me`, { withCredentials: true });
      const destination = meResponse.data.onboarding_completed ? '/dashboard' : '/onboarding';
      navigate(destination);
    } catch (err) {
      toast.error(t('landing.auth.devLoginFailed'));
      console.error(err);
    }
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const greetings = [
    // Row 1 - Top
    "Olá", "Hola", "Bonjour", "你好", "こんにちは", "Ciao",
    // Row 2
    "안녕하세요", "مرحبا", "سلام", "Привет", "שלום", "Γεια σου",
    // Row 3
    "नमस्ते", "Habari", "Xin chào", "Sawubona", "Hei", "Hallo",
    // Row 4 - Bottom
    "Cześć", "Merhaba", "สวัสดี", "Buna", "Ahoj", "Sveiki",
  ];

  const languages = [
    "English", "Português", "Español", "Français", "中文", "العربية",
    "Русский", "日本語", "한국어", "हिन्दी", "فارسی", "Kiswahili",
    "Italiano", "Deutsch", "Türkçe", "Tiếng Việt", "Polski", "ελληνικά",
  ];

  const countries = [
    { left: "10%", top: "36%", size: 12 },
    { left: "16%", top: "42%", size: 10 },
    { left: "21%", top: "33%", size: 11 },
    { left: "28%", top: "49%", size: 10 },
    { left: "34%", top: "31%", size: 11 },
    { left: "42%", top: "40%", size: 10 },
    { left: "49%", top: "34%", size: 10 },
    { left: "53%", top: "47%", size: 11 },
    { left: "58%", top: "37%", size: 10 },
    { left: "64%", top: "29%", size: 11 },
    { left: "70%", top: "39%", size: 10 },
    { left: "77%", top: "33%", size: 11 },
    { left: "84%", top: "50%", size: 10 },
    { left: "88%", top: "41%", size: 11 },
  ];

  const forge = [
    ["F", "Frequency"],
    ["O", "Observation"],
    ["R", "Reinforcement"],
    ["G", "Growth"],
    ["E", "Exposure"],
  ];

  const tiers = [
    {
      key: 'free',
      features: ['50 credits to try', 'Basic reader', 'Progress tracking'],
    },
    {
      key: 'starter',
      features: ['110 credits', 'Full reader access', 'Basic review tools'],
    },
    {
      key: 'popular',
      features: ['400 credits', 'AI assistance', 'Advanced statistics', 'Priority support'],
    },
    {
      key: 'best',
      features: ['950 credits', 'All features unlocked', 'Forum perks', 'Priority features'],
    },
  ];

  const learnerTypeKeys = ['polyglot', 'serious', 'casual', 'linguist', 'student', 'tech'];

  // Section navigation component
  const SectionNav = ({ prevSection, prevLabel, nextSection, nextLabel }) => (
    <>
      {prevSection && (
        <button
          onClick={() => scrollToSection(prevSection)}
          className="absolute top-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 hover:text-white/70 transition-colors group z-20"
        >
          <ChevronUp className="h-5 w-5 group-hover:-translate-y-1 transition-transform" />
          <span className="text-xs uppercase tracking-wider">{prevLabel}</span>
        </button>
      )}
      {nextSection && (
        <button
          onClick={() => scrollToSection(nextSection)}
          className="absolute bottom-4 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 text-white/40 hover:text-white/70 transition-colors group z-20"
        >
          <span className="text-xs uppercase tracking-wider">{nextLabel}</span>
          <ChevronDown className="h-5 w-5 group-hover:translate-y-1 transition-transform" />
        </button>
      )}
    </>
  );

  const sectionTitle = (title) => (
    <div className="text-3xl md:text-4xl font-bold mb-8 md:mb-10 text-center">
      {title}
    </div>
  );

  return (
    <div className="site-bg snap-y snap-mandatory h-screen overflow-y-auto scroll-smooth">
      {/* Hero Section */}
      <section id="hero" className="relative min-h-screen h-screen snap-start border-b border-white/10 flex flex-col">
        <div className="absolute inset-0 grid-overlay opacity-60" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,rgba(255,255,255,0.02),transparent_42%)]" />

        {greetings.map((word, i) => {
          const row = Math.floor(i / 6);
          const col = i % 6;
          const baseLeft = 3 + col * 15 + (row % 2) * 7;
          const baseTop = 8 + row * 20;
          const offsetX = (i * 7) % 10 - 5;
          const offsetY = (i * 11) % 8 - 4;
          
          return (
            <div
              key={`${word}-${i}`}
              className={`floating-greeting absolute font-semibold select-none pointer-events-none ${
                i % 3 === 0 ? "text-[#ffcc33]/90" : 
                i % 3 === 1 ? "text-[#d7e7ff]/80" : 
                "text-[#7ee787]/70"
              }`}
              style={{
                left: `${baseLeft + offsetX}%`,
                top: `${baseTop + offsetY}%`,
                fontSize: `${16 + (i % 4) * 6}px`,
                '--drift-duration': `${6 + (i % 5) * 2}s`,
                '--drift-delay': `${(i % 8) * 0.5}s`,
              }}
            >
              {word}
            </div>
          );
        })}

        <div className="relative z-10 max-w-7xl mx-auto px-6 py-8 md:py-10 flex flex-col min-h-screen">
          <header className="flex items-center justify-between">
            <div>
              <div className="text-2xl md:text-3xl font-black tracking-tight">iLing</div>
              <div className="text-sm text-white/60 mt-1">{t('landing.tagline')}</div>
            </div>
            <nav className="flex items-center gap-3 md:gap-5">
              <a href="#philosophy" className="hidden md:block text-sm text-white/75 hover:text-white transition">{t('landing.nav.philosophy')}</a>
              <a href="#reader" className="hidden md:block text-sm text-white/75 hover:text-white transition">{t('landing.nav.reader')}</a>
              <a href="#community" className="hidden md:block text-sm text-white/75 hover:text-white transition">{t('landing.nav.community')}</a>
              <a href="#pricing" className="hidden md:block text-sm text-white/75 hover:text-white transition">{t('landing.nav.pricing')}</a>
              <LanguagePicker variant="desktop" />
              <button
                onClick={() => setShowAuthModal(true)}
                data-testid="nav-signin-btn"
                className="px-4 py-2 rounded-lg border border-white/15 bg-white/5 hover:bg-white/10 transition text-sm font-medium"
              >
                {t('landing.nav.signIn')}
              </button>
            </nav>
          </header>

          <div className="flex-1 flex flex-col justify-center items-center py-8 md:py-16">
            <div className="text-center max-w-4xl px-2">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#ffcc33]/20 bg-[#ffcc33]/8 text-[#ffe27d] text-xs uppercase tracking-[0.22em] mb-4 md:mb-6">
                {t('landing.hero.badge')}
              </div>
              <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tight leading-[0.95]">
                {t('landing.hero.titleLine1')}
                <span className="block text-[#ffcc33]">{t('landing.hero.titleLine2')}</span>
              </h1>
              <p className="mt-4 md:mt-6 max-w-2xl mx-auto text-base md:text-xl text-white/78 leading-relaxed px-2">
                {t('landing.hero.subtitle')}
              </p>
              <div className="mt-6 md:mt-8 flex flex-wrap justify-center gap-4">
                <button
                  onClick={() => setShowAuthModal(true)}
                  data-testid="hero-start-learning-btn"
                  className="px-6 py-3 md:px-7 md:py-4 rounded-2xl bg-[#3A7BFF] hover:bg-[#4c89ff] transition font-semibold shadow-[0_0_22px_rgba(58,123,255,0.16)]"
                >
                  {t('landing.hero.startLearning')}
                </button>
              </div>
              <div className="mt-5 md:mt-7 text-xs md:text-sm text-white/58 max-w-2xl mx-auto leading-relaxed px-4">
                {t('landing.hero.afterSignup')}
              </div>
              
              {/* Video section removed for production */}
            </div>
          </div>

          {/* Language by the Numbers - Clickable scroll button */}
          <div className="pb-6 md:pb-8 flex justify-center">
            <button
              onClick={() => scrollToSection('numbers')}
              className="group flex flex-col items-center gap-2 md:gap-3 px-5 py-3 md:px-8 md:py-5 rounded-2xl border border-white/15 bg-white/5 hover:bg-white/10 hover:border-[#ffcc33]/30 transition-all"
              data-testid="scroll-to-numbers-btn"
            >
              <span className="text-sm md:text-lg font-semibold text-white/90 group-hover:text-[#ffcc33] transition-colors">
                {t('landing.hero.scrollLabel')}
              </span>
              <ChevronDown className="h-4 w-4 md:h-5 md:w-5 text-white/50 group-hover:text-[#ffcc33] group-hover:translate-y-1 transition-all" />
            </button>
          </div>
        </div>
      </section>

      {/* Language by the Numbers - Live System Preview Section */}
      <section id="numbers" className="relative min-h-screen md:h-screen snap-start flex flex-col justify-center border-b border-white/10 py-8 md:py-0">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-12">
        <SectionNav 
          prevSection="hero" 
          prevLabel={t('landing.sections.home')} 
          nextSection="philosophy" 
          nextLabel={t('landing.sections.philosophy')} 
        />
        
        {sectionTitle(t('landing.numbers.title'))}
        
        <div className="flex justify-center">
          <div className="relative w-full max-w-3xl">
            <div className="absolute -inset-6 bg-[radial-gradient(circle,rgba(58,123,255,0.09),transparent_55%)] blur-2xl" />
            <div className="relative glass rounded-[28px] overflow-hidden shadow-2xl shadow-black/30">
              <div className="flex items-center justify-between px-5 py-4 border-b border-white/10 bg-white/5">
                <div className="flex items-center gap-2">
                  <span className="w-3 h-3 rounded-full bg-white/25" />
                  <span className="w-3 h-3 rounded-full bg-white/18" />
                  <span className="w-3 h-3 rounded-full bg-white/12" />
                </div>
                <div className="text-xs uppercase tracking-[0.22em] text-white/45">
                  {t('landing.numbers.livePreview')}
                </div>
              </div>

              <div className="p-5 md:p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="rounded-2xl border border-white/10 bg-[#091624] p-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">{t('landing.numbers.knownWords')}</div>
                    <div className="mt-3 text-3xl font-bold">2,143</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-[#091624] p-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">{t('landing.numbers.target')}</div>
                    <div className="mt-3 text-3xl font-bold text-[#ffcc33]">2K</div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-[#091624] p-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">{t('landing.numbers.streak')}</div>
                    <div className="mt-3 text-3xl font-bold text-[#7ee787]">19</div>
                  </div>
                </div>

                <div className="rounded-3xl border border-white/10 bg-[#07111c] overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10 bg-white/5 text-sm text-white/65">
                    <span>{t('landing.numbers.chineseReader')}</span>
                    <span>{t('landing.numbers.hskLevel')}</span>
                  </div>
                  <div className="p-5">
                    <p className="text-xl leading-loose tracking-wide text-white/90">
                      我喜欢学习语言，因为每一种语言都打开一个新的世界。
                    </p>
                    <div className="mt-4 flex flex-wrap gap-2 text-sm">
                      {[
                        ["我", "I"],
                        ["喜欢", "like"],
                        ["学习", "study"],
                        ["语言", "language"],
                        ["世界", "world"],
                      ].map(([word, meaning]) => (
                        <span
                          key={word}
                          className="px-3 py-2 rounded-xl bg-white/6 border border-white/10 text-white/80"
                        >
                          {word} <span className="text-[#ffcc33]">· {meaning}</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="grid md:grid-cols-[1fr_auto] gap-3">
                  <div className="rounded-2xl border border-white/10 bg-[#091624] p-4">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">{t('landing.numbers.flow')}</div>
                    <div className="mt-3 flex flex-wrap items-center gap-2 text-sm md:text-base">
                      {[
                        ['text', t('landing.flowSteps.text')],
                        ['words', t('landing.flowSteps.words')],
                        ['context', t('landing.flowSteps.context')],
                        ['strength', t('landing.flowSteps.strength')],
                        ['fluency', t('landing.flowSteps.fluency')],
                      ].map(([key, label], idx) => (
                        <div key={key} className="flex items-center gap-2">
                          <span className={
                            key === 'strength' ? 'text-[#7ee787]' :
                            key === 'fluency' ? 'text-[#ffcc33]' : 'text-white/85'
                          }>
                            {label}
                          </span>
                          {idx < 4 && <span className="text-white/35">→</span>}
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-[#091624] p-4 min-w-[170px]">
                    <div className="text-[11px] uppercase tracking-[0.18em] text-white/45">{t('landing.numbers.wordStrength')}</div>
                    <div className="mt-3 text-2xl font-bold text-[#7ee787]">0.73</div>
                    <div className="mt-1 text-sm text-white/60">{t('landing.numbers.strongAndGrowing')}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section id="philosophy" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="numbers" 
          prevLabel={t('landing.sections.numbers')} 
          nextSection="reader" 
          nextLabel={t('landing.sections.reader')} 
        />
        
        {sectionTitle(t('landing.philosophy.title'))}
        <div className="grid lg:grid-cols-2 gap-8 md:gap-10">
          <div className="glass rounded-[28px] p-8 md:p-10">
            <p className="text-lg text-white/80 leading-relaxed">
              {t('landing.philosophy.intro')}
            </p>
            <div className="mt-6 space-y-4 text-white/75">
              <div>• {t('landing.philosophy.bullet1')}</div>
              <div>• {t('landing.philosophy.bullet2')}</div>
              <div>• {t('landing.philosophy.bullet3')}</div>
              <div>• {t('landing.philosophy.bullet4')}</div>
            </div>
          </div>
          <div className="glass rounded-[28px] p-8 md:p-10 flex items-center justify-center">
            <div className="w-full max-w-xl">
              <div className="grid grid-cols-5 gap-3 text-center text-sm md:text-base font-semibold">
                {[
                  ['text', t('landing.flowSteps.text')],
                  ['words', t('landing.flowSteps.words')],
                  ['context', t('landing.flowSteps.context')],
                  ['strength', t('landing.flowSteps.strength')],
                  ['fluency', t('landing.flowSteps.fluency')],
                ].map(([key, label], idx) => (
                  <div
                    key={key}
                    className="relative rounded-2xl border border-white/10 bg-white/5 px-2 py-5"
                  >
                    <div className={
                      key === 'strength' ? 'text-[#7ee787]' :
                      key === 'fluency' ? 'text-[#ffcc33]' : 'text-white/85'
                    }>
                      {label}
                    </div>
                    {idx < 4 && (
                      <div className="hidden md:block absolute -right-2 top-1/2 -translate-y-1/2 text-white/30">→</div>
                    )}
                  </div>
                ))}
              </div>
              <div className="mt-8 text-sm text-white/58 text-center">
                {t('landing.philosophy.flowCaption')}
              </div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Reader Section */}
      <section id="reader" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="philosophy" 
          prevLabel={t('landing.sections.philosophy')} 
          nextSection="global" 
          nextLabel={t('landing.sections.globalEngine')} 
        />
        
        {sectionTitle(t('landing.reader.title'))}
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              title: t('landing.reader.smartDetectionTitle'),
              body: t('landing.reader.smartDetectionBody'),
            },
            {
              title: t('landing.reader.popoverTitle'),
              body: t('landing.reader.popoverBody'),
            },
            {
              title: t('landing.reader.strengthTitle'),
              body: t('landing.reader.strengthBody'),
            },
          ].map((card) => (
            <div key={card.title} className="glass rounded-[28px] p-7">
              <div className="text-xl font-semibold mb-3">{card.title}</div>
              <p className="text-white/76 leading-relaxed">{card.body}</p>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* Global Language Engine Section */}
      <section id="global" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="reader" 
          prevLabel={t('landing.sections.reader')} 
          nextSection="dashboard" 
          nextLabel={t('landing.sections.dashboard')} 
        />
        
        {sectionTitle(t('landing.global.title'))}
        <div className="glass rounded-[32px] p-6 md:p-10 overflow-hidden">
          <div className="relative h-[360px] md:h-[440px] rounded-[28px] border border-white/10 bg-[linear-gradient(180deg,#0a1624,#07111c)] overflow-hidden">
            <div className="absolute inset-0 opacity-80 map-shape bg-[linear-gradient(180deg,rgba(255,255,255,0.08),rgba(255,255,255,0.03))] border border-white/5" />
            <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 500" fill="none">
              <path d="M120 230 C250 160, 360 160, 500 235" stroke="rgba(255,255,255,0.18)" strokeWidth="1.4" />
              <path d="M500 235 C620 150, 740 160, 860 220" stroke="rgba(255,255,255,0.18)" strokeWidth="1.4" />
              <path d="M200 290 C360 250, 510 270, 740 310" stroke="rgba(255,255,255,0.14)" strokeWidth="1.2" />
              <path d="M330 180 C430 250, 540 260, 650 180" stroke="rgba(255,204,51,0.16)" strokeWidth="1.2" />
              <path d="M720 215 C790 270, 840 300, 900 250" stroke="rgba(126,231,135,0.15)" strokeWidth="1.2" />
            </svg>
            {countries.map((dot, i) => (
              <div
                key={i}
                className="absolute rounded-full bg-[#ffdd7d] shadow-[0_0_10px_rgba(255,221,125,0.35)]"
                style={{
                  left: dot.left,
                  top: dot.top,
                  width: `${dot.size}px`,
                  height: `${dot.size}px`,
                  animation: `pulseDot ${3.2 + (i % 4) * 0.7}s ease-in-out infinite`,
                }}
              />
            ))}
            <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-center text-white/70 text-sm max-w-xl px-4">
              {t('landing.global.caption')}
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Dashboard Section */}
      <section id="dashboard" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="global" 
          prevLabel={t('landing.sections.globalEngine')} 
          nextSection="forge" 
          nextLabel={t('landing.sections.forgeMethod')} 
        />
        
        {sectionTitle(t('landing.dashboard.title'))}
        <div className="grid md:grid-cols-3 gap-6">
          <div className="glass rounded-[28px] p-7">
            <div className="text-lg font-semibold">{t('landing.dashboard.knownWords')}</div>
            <div className="mt-5 space-y-4 text-white/80">
              <div className="flex items-center justify-between"><span>Chinese</span><span>812</span></div>
              <div className="flex items-center justify-between"><span>Arabic</span><span>531</span></div>
              <div className="flex items-center justify-between"><span>French</span><span>204</span></div>
            </div>
          </div>
          <div className="glass rounded-[28px] p-7">
            <div className="text-lg font-semibold">{t('landing.dashboard.learningVelocity')}</div>
            <div className="mt-5 space-y-4 text-white/80">
              <div className="flex items-center justify-between"><span>{t('landing.dashboard.wordsPerWeek')}</span><span>126</span></div>
              <div className="flex items-center justify-between"><span>{t('landing.dashboard.exposureHours')}</span><span>5.4</span></div>
              <div className="flex items-center justify-between"><span>{t('landing.dashboard.retentionRate')}</span><span>89%</span></div>
            </div>
          </div>
          <div className="glass rounded-[28px] p-7">
            <div className="text-lg font-semibold">{t('landing.dashboard.dailyMission')}</div>
            <div className="mt-5 space-y-4 text-white/80">
              <div>✓ {t('landing.dashboard.mission1')}</div>
              <div>✓ {t('landing.dashboard.mission2')}</div>
              <div>✓ {t('landing.dashboard.mission3')}</div>
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* FORGE Method Section */}
      <section id="forge" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="dashboard" 
          prevLabel={t('landing.sections.dashboard')} 
          nextSection="ai" 
          nextLabel={t('landing.sections.aiIntelligence')} 
        />
        
        {sectionTitle(t('landing.forge.title'))}
        <div className="grid md:grid-cols-5 gap-4">
          {forge.map(([letter]) => (
            <div key={letter} className="glass rounded-[24px] p-6 text-center">
              <div className="text-4xl md:text-5xl font-black text-[#ffcc33]">{letter}</div>
              <div className="mt-3 text-lg font-semibold">{t(`landing.forge.${letter}`)}</div>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* AI Section */}
      <section id="ai" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="forge" 
          prevLabel={t('landing.sections.forgeMethod')} 
          nextSection="pricing" 
          nextLabel={t('landing.sections.pricing')} 
        />
        
        {sectionTitle(t('landing.ai.title'))}
        <div className="grid lg:grid-cols-[.95fr_1.05fr] gap-6 md:gap-8">
          <div className="glass rounded-[28px] p-8">
            <div className="text-2xl font-semibold leading-snug">
              {t('landing.ai.heading')}
            </div>
            <p className="mt-5 text-white/76 leading-relaxed">
              {t('landing.ai.body')}
            </p>
          </div>
          <div className="glass rounded-[28px] p-8">
            <div className="text-sm uppercase tracking-[0.22em] text-white/45 mb-5">
              {t('landing.ai.selfIdLabel')}
            </div>
            <div className="flex flex-wrap gap-3">
              {learnerTypeKeys.map((key) => (
                <span
                  key={key}
                  className="px-4 py-2 rounded-full border border-white/10 bg-white/5 text-white/80"
                >
                  {t(`landing.ai.types.${key}`)}
                </span>
              ))}
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* Language ticker */}
      <section className="py-10 border-t border-b border-white/10 overflow-hidden snap-start">
        <div className="word-row flex gap-10 text-lg md:text-2xl font-semibold text-white/70 whitespace-nowrap px-6">
          {[...languages, ...languages].map((lang, i) => (
            <span
              key={`${lang}-${i}`}
              className={
                i % 4 === 0 ? "text-[#ffcc33]" :
                i % 5 === 0 ? "text-[#7ee787]" : "text-white/70"
              }
            >
              {lang}
            </span>
          ))}
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="ai" 
          prevLabel={t('landing.sections.aiIntelligence')} 
          nextSection="community" 
          nextLabel={t('landing.sections.community')} 
        />
        
        {sectionTitle(t('landing.pricing.title'))}
        <div className="grid md:grid-cols-3 gap-6">
          {tiers.map((tier, index) => (
            <div
              key={tier.key}
              className={`rounded-[30px] p-8 border ${
                index === 1
                  ? "border-[#ffcc33]/40 bg-[linear-gradient(180deg,rgba(255,204,51,.09),rgba(255,255,255,.03))]"
                  : "border-white/10 bg-[linear-gradient(180deg,rgba(255,255,255,.07),rgba(255,255,255,.03))]"
              }`}
            >
              <div className="text-xl font-semibold">{t(`landing.pricing.${tier.key}.name`)}</div>
              <div className={`mt-4 text-4xl font-black ${
                index === 1 ? "text-[#ffcc33]" :
                index === 2 ? "text-[#7ee787]" : "text-white"
              }`}>
                {t(`landing.pricing.${tier.key}.price`)}
              </div>
              <p className="mt-4 text-white/72 leading-relaxed">{t(`landing.pricing.${tier.key}.description`)}</p>
              <div className="mt-6 space-y-3 text-white/80">
                {(tRaw(`landing.pricing.${tier.key}.features`) || tier.features).map((feature, i) => (
                  <div key={i}>• {feature}</div>
                ))}
              </div>
            </div>
          ))}
        </div>
        </div>
      </section>

      {/* Community Section */}
      <section id="community" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="pricing" 
          prevLabel={t('landing.sections.pricing')} 
          nextSection="cta" 
          nextLabel={t('landing.sections.getStarted')} 
        />
        
        {sectionTitle(t('landing.community.title'))}
        <div className="grid md:grid-cols-2 gap-6">
          <div className="glass rounded-[28px] p-8">
            <div className="text-2xl font-semibold">{t('landing.community.forumTitle')}</div>
            <p className="mt-4 text-white/76 leading-relaxed">
              {t('landing.community.forumBody')}
            </p>
          </div>
          <div className="glass rounded-[28px] p-8">
            <div className="text-2xl font-semibold">{t('landing.community.platformTitle')}</div>
            <p className="mt-4 text-white/76 leading-relaxed">
              {t('landing.community.platformBody')}
            </p>
          </div>
        </div>
        </div>
      </section>

      {/* CTA Section */}
      <section id="cta" className="relative min-h-screen h-screen snap-start flex flex-col justify-center border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-12">
        <SectionNav 
          prevSection="community" 
          prevLabel={t('landing.sections.community')} 
        />
        
        <div className="glass rounded-[34px] p-10 md:p-14 text-center">
          <div className="text-3xl md:text-5xl font-black tracking-tight max-w-4xl mx-auto leading-tight">
            {t('landing.cta.titleLine1')}
            <span className="block text-[#ffcc33]">{t('landing.cta.titleLine2')}</span>
          </div>
          <div className="mt-6 text-white/70 max-w-2xl mx-auto text-lg">
            {t('landing.cta.body')}
          </div>
          <div className="mt-8 flex flex-wrap justify-center gap-4">
            <button
              onClick={() => setShowAuthModal(true)}
              data-testid="cta-start-learning-btn"
              className="px-7 py-4 rounded-2xl bg-[#3A7BFF] hover:bg-[#4c89ff] transition font-semibold shadow-[0_0_22px_rgba(58,123,255,0.16)]"
            >
              {t('landing.cta.startLearning')}
            </button>
          </div>
        </div>
        </div>
      </section>
      
      {/* Auth Modal */}
      <AuthModal 
        isOpen={showAuthModal} 
        onClose={() => {
          setShowAuthModal(false);
          setInitialResetToken('');
        }}
        initialResetToken={initialResetToken}
      />
    </div>
  );
}
